/* import __COLOCATED_TEMPLATE__ from './bridge-chart-drill-in.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import { getOwner } from '@ember/application';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type ReportingDrillInBridgeService from 'embercom/services/reporting-drill-in-bridge';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { action } from '@ember/object';
import type Chart from 'embercom/models/reporting/custom/chart';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type { DrillInViewType } from 'embercom/components/reporting/custom/report/underlying-data-modal';
import type AppService from 'embercom/services/app-service';
import type ApplicationInstance from '@ember/application/instance';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { type ReportingDrillInBridgeOpenPayload } from 'embercom/services/reporting-drill-in-bridge';

interface Signature {
  Args: {};
}

export default class ReportingCustomReportBridgeChartDrillIn extends Component<Signature> {
  @service declare store: Store;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare reportingDrillInBridge: ReportingDrillInBridgeService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare appService: AppService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  constructor(owner: ApplicationInstance, args: {}) {
    super(owner, args);
    let unsubscribeOpen = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'reporting:drill-in:open',
      (data: ReportingDrillInBridgeOpenPayload) => {
        this.reportingDrillInBridge.open(data);
      },
    );

    let unsubscribeClose = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'reporting:drill-in:close',
      this.onClose,
    );

    registerDestructor(this, () => {
      unsubscribeOpen();
      unsubscribeClose();
    });
  }

  get payload() {
    return this.reportingDrillInBridge.payload;
  }

  get isOpen() {
    return this.reportingDrillInBridge.isOpen && !!this.chart;
  }

  get chart(): Chart | null {
    return this.reportingDrillInBridge.chartRecord;
  }

  get renderableChart(): RenderableChart | null {
    if (!this.chart) {
      return null;
    }
    let reportState = {
      filters: this.payload?.chartDefinition?.filters || {},
      timezone: this.appService?.app?.timezone || 'UTC',
    };
    return new RenderableChart(this.chart, reportState, getOwner(this));
  }

  get chartSeries(): ChartSeries | undefined {
    let series = this.chart?.chartSeries;
    if (!series) {
      return undefined;
    }
    let idx = this.payload?.selectedSeriesIndex;
    if (typeof idx === 'number' && idx >= 0 && idx < series.length) {
      let match = series.objectAt(idx);
      if (match) {
        return match;
      }
    }
    return series.firstObject as ChartSeries | undefined;
  }

  get knownValuesEndpointSources(): string[] {
    return this.chart?.chartSeries.map((series: ChartSeries) => series.metric.firstSource) ?? [];
  }

  get eventPlaceForDrillIn(): string {
    return this.payload?.eventPlace ?? 'ai_categories';
  }

  get viewsForDrillIn(): DrillInViewType[] | undefined {
    let views = this.payload?.views;
    if (!views) {
      return undefined;
    }
    return views.filter((v: string): v is DrillInViewType => v === 'table');
  }

  get conversationAttributeDescriptors() {
    return this.store.peekAll('conversation-attributes/descriptor').toArray();
  }

  get columns(): string[] {
    let renderableChart = this.renderableChart;
    let chartSeries = this.chartSeries;
    if (!this.chart || !renderableChart || !chartSeries) {
      return [];
    }

    if (this.payload?.columns && this.payload.columns.length > 0) {
      return this.payload.columns;
    }

    return this.reportingUnderlyingDataService.getDefaultColumns(
      chartSeries,
      renderableChart,
      Boolean(this.payload?.isStandalone),
    );
  }

  // action to allow changing columns in the drill in view
  @action
  setColumns(chartSeries: ChartSeries, columns: string[]) {
    let renderableChart = this.renderableChart;
    if (!renderableChart) {
      return;
    }
    let currentColumns =
      this.payload?.columns && this.payload.columns.length > 0
        ? this.payload.columns
        : this.reportingUnderlyingDataService.getDefaultColumns(
            chartSeries,
            renderableChart,
            Boolean(this.payload?.isStandalone),
          );

    let ordered = this.reportingUnderlyingDataService.getOrderedAttributeIds(
      chartSeries.metric,
      columns,
      currentColumns,
    );

    if (!this.payload) {
      return;
    }
    this.reportingDrillInBridge.payload = {
      ...this.payload,
      columns: ordered,
    };
  }

  onClose = () => {
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('reporting:drill-in:closed');
    this.reportingDrillInBridge.close();
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::BridgeChartDrillIn': typeof ReportingCustomReportBridgeChartDrillIn;
    'reporting/custom/report/bridge-chart-drill-in': typeof ReportingCustomReportBridgeChartDrillIn;
  }
}
